<script setup>
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import { ContractsAPI } from 'dashboard/api/talkviaContracts';

// Franquias do ciclo do plano (contratos e biometria) e saldos comprados, para o
// administrador acompanhar. Abrir o painel só lê os saldos; nada é consumido.
const router = useRouter();
const dialogRef = ref(null);
const credits = ref(null);
const isLoading = ref(false);
const loadError = ref('');

const formatDate = value => {
  if (!value) return '';
  const date =
    value.length === 10 ? new Date(`${value}T12:00:00`) : new Date(value);
  return date.toLocaleDateString('pt-BR');
};

const cycleDescription = computed(() => {
  const cycle = credits.value?.cycle;
  if (!cycle) return '';
  const origin = cycle.renewal_based
    ? `Ciclo iniciado na renovação do plano em ${formatDate(cycle.started_at)}`
    : `Ciclo do mês, iniciado em ${formatDate(cycle.started_at)}`;
  const next = cycle.next_renewal_on
    ? ` · próxima renovação prevista para ${formatDate(cycle.next_renewal_on)}`
    : '';
  return `${origin}${next}.`;
});

const cards = computed(() => {
  const data = credits.value;
  if (!data) return [];
  const { contracts, biometric, reinforced } = data;
  return [
    {
      key: 'contracts',
      icon: 'i-hugeicons-contracts',
      title: 'Contratos',
      tag: 'Assinatura simples e reforçada',
      value: contracts.unlimited
        ? 'Ilimitado'
        : `${contracts.remaining_from_plan} de ${contracts.monthly_limit}`,
      valueHint: 'restantes da franquia neste ciclo',
      rows: [
        ['Usados da franquia', contracts.used_from_plan],
        ['Avulsos usados no ciclo', contracts.used_from_credits],
        ['Créditos comprados em saldo', contracts.credit_balance],
      ],
    },
    {
      key: 'biometric',
      icon: 'i-hugeicons-finger-print-scan',
      title: 'Biometria',
      tag: 'Assinatura biométrica Serpro',
      value: `${biometric.remaining_from_plan} de ${biometric.monthly_limit}`,
      valueHint: 'restantes da franquia neste ciclo',
      rows: [
        ['Usadas da franquia', biometric.used_from_plan],
        ['Créditos usados no ciclo', biometric.used_from_credits],
        ['Créditos comprados em saldo', biometric.credit_balance],
      ],
    },
    {
      key: 'reinforced',
      icon: 'i-hugeicons-user-check-01',
      title: 'Conferências de dados',
      tag: 'Usadas na assinatura reforçada',
      value: String(reinforced?.balance ?? 0),
      valueHint: 'consultas do Talkvia Cadastro disponíveis',
      rows: [],
    },
  ];
});

const load = async () => {
  isLoading.value = true;
  loadError.value = '';
  try {
    const { data } = await ContractsAPI.credits();
    credits.value = data;
  } catch (error) {
    loadError.value =
      error?.response?.data?.error ||
      'Não foi possível carregar os créditos. Tente novamente.';
  } finally {
    isLoading.value = false;
  }
};

const open = () => {
  dialogRef.value?.open();
  load();
};

const openAllCredits = () => {
  dialogRef.value?.close();
  router.push({ name: 'billing_credits_index' });
};

const openMarketplace = () => {
  dialogRef.value?.close();
  router.push({ name: 'talkvia_shop' });
};

defineExpose({ open });
</script>

<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<template>
  <Dialog
    ref="dialogRef"
    title="Créditos de contratos"
    description="Franquias incluídas no plano, renovadas a cada mensalidade paga, e créditos comprados no Marketplace."
    width="3xl"
    overflow-y-auto
    confirm-button-label="Comprar créditos"
    cancel-button-label="Fechar"
    @confirm="openMarketplace"
  >
    <div
      v-if="isLoading"
      class="flex items-center gap-2 py-6 text-sm text-n-slate-11"
      role="status"
    >
      <span class="i-hugeicons-loading-03 size-4 animate-spin" />
      Carregando…
    </div>
    <div v-else-if="loadError" class="flex flex-col items-start gap-3 py-4">
      <p class="mb-0 text-sm text-n-ruby-11">{{ loadError }}</p>
      <button
        type="button"
        class="text-sm font-medium text-n-brand hover:underline"
        @click="load"
      >
        Tentar novamente
      </button>
    </div>
    <div v-else-if="credits" class="grid gap-4">
      <p class="mb-0 text-sm text-n-slate-11">
        <span
          class="i-hugeicons-calendar-03 mr-1 inline-block size-4 align-middle"
        />
        {{ cycleDescription }}
        O que sobrar da franquia não acumula; créditos comprados não expiram.
      </p>
      <div class="grid gap-3 md:grid-cols-3">
        <article
          v-for="card in cards"
          :key="card.key"
          class="flex flex-col rounded-xl border border-n-weak bg-n-solid-2 p-4"
        >
          <header class="flex items-start gap-3">
            <span
              class="grid size-10 shrink-0 place-content-center rounded-xl bg-n-brand/10 text-n-brand"
            >
              <i :class="card.icon" class="size-5" />
            </span>
            <div class="min-w-0">
              <strong class="block text-sm text-n-slate-12">{{
                card.title
              }}</strong>
              <span class="block text-xs text-n-slate-11">{{ card.tag }}</span>
            </div>
          </header>
          <strong class="mt-3 text-2xl font-semibold text-n-slate-12">{{
            card.value
          }}</strong>
          <span class="text-xs text-n-slate-11">{{ card.valueHint }}</span>
          <dl
            v-if="card.rows.length"
            class="mb-0 mt-3 grid gap-1 border-t border-n-weak pt-3 text-xs"
          >
            <div
              v-for="[label, amount] in card.rows"
              :key="label"
              class="flex justify-between gap-2"
            >
              <dt class="text-n-slate-11">{{ label }}</dt>
              <dd class="mb-0 font-semibold tabular-nums text-n-slate-12">
                {{ amount }}
              </dd>
            </div>
          </dl>
        </article>
      </div>
      <p class="mb-0 text-xs text-n-slate-10">
        Cada contrato gerado usa primeiro a franquia do plano e depois os
        créditos comprados. A assinatura biométrica usa também uma biometria por
        validação.
      </p>
      <button
        type="button"
        class="w-fit text-sm font-medium text-n-brand hover:underline"
        @click="openAllCredits"
      >
        Ver todos os créditos da empresa
      </button>
    </div>
  </Dialog>
</template>
